import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { initI18n } from '@ferro/core/i18n';
import { installBrandStyles } from './brand.js';

// Initialize the shared i18n singleton once on module load. Same
// pattern as customer-app — reads saved language from localStorage
// (shared storage key across both apps), falls back to English.
initI18n();
import { AppShell } from './components/AppShell.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
// #551 — provisioning gate (does this facility HAVE the feature), the
// sibling of RouteGuard's role gate (may this person use it).
import { FeatureRoute } from './components/FeatureRoute.js';
import { NotificationProvider } from './components/NotificationCenter.js';
import { PlatformAdminRoute } from './components/PlatformAdminRoute.js';
import { ProtectedRoute } from './components/ProtectedRoute.js';
import { RouteGuard } from './components/RouteGuard.js';
import { ServerWakingBanner } from './components/ServerWakingBanner.js';
import { AuthProvider } from './contexts/AuthContext.js';
import { personaHomeRoute, ROUTE_ACCESS_RULES } from './lib/route-permissions.js';
import { useAuth } from './contexts/AuthContext.js';
import { AiManager } from './pages/AiManager.js';
import { Audit } from './pages/Audit.js';
import { Bookings } from './pages/Bookings.js';
import { Compliance } from './pages/Compliance.js';
import { VaxIntake } from './pages/VaxIntake.js';
import { CustomerDetail } from './pages/CustomerDetail.js';
import { Customers } from './pages/Customers.js';
// #553 — dedicated Pet Profile page (deep-linked from the customer record's
// dog cards, the Dog ID Card modal, and search). Gated on pets:read.
import { DogDetail } from './pages/DogDetail.js';
import { Dashboard } from './pages/Dashboard.js';
// #548 — Front-Desk simple mode: one-tap check-in surface for daily staff.
// Gated on bookings.daycare-boarding:write (owner/manager/receptionist).
import { FrontDesk } from './pages/FrontDesk.js';
import { Incidents } from './pages/Incidents.js';
import { IncidentNew } from './pages/IncidentNew.js';
import { IncidentDetail } from './pages/IncidentDetail.js';
import { IntakePortal } from './pages/intake/IntakePortal.js';
import { InviteAccept } from './pages/InviteAccept.js';
import { StaffForgotPassword } from './pages/StaffForgotPassword.js';
import { StaffResetPassword } from './pages/StaffResetPassword.js';
import { Login } from './pages/Login.js';
import { Loyalty } from './pages/Loyalty.js';
import { Payments } from './pages/Payments.js';
import { PricingRules } from './pages/PricingRules.js';
import { IntakeDetail } from './pages/platform/IntakeDetail.js';
import { IntakeList } from './pages/platform/IntakeList.js';
import { IntakeNew } from './pages/platform/IntakeNew.js';
import { TenantDetail } from './pages/platform/TenantDetail.js';
import { TenantsList } from './pages/platform/TenantsList.js';
import { TenantsNew } from './pages/platform/TenantsNew.js';
import { Reports } from './pages/Reports.js';
import { Schedule } from './pages/Schedule.js';
import { CalendarDay } from './pages/CalendarDay.js';
import { LodgingCalendar } from './pages/LodgingCalendar.js';
import { Settings } from './pages/Settings.js';
// #427 W3 — Timesheets & Payroll export. Money-facing; gated on
// payroll:read:all (owner/manager/accountant) via ROUTE_ACCESS_RULES.
import { Timesheets } from './pages/Timesheets.js';
// #427 W2 — a staffer's own week (#400). walker/groomer/kennel_tech (whose
// own-week focus survives the #665 D1 read:all upgrade via the server's
// ?self=1 narrowing) + accountant (#665 — held time-off:request:self with
// no schedule surface to exercise it from).
import { MySchedule } from './pages/MySchedule.js';
import { Staff } from './pages/Staff.js';
// #427 W1 — time clock surfaces. Kiosk is PUBLIC (device-token auth, no
// staff JWT — spec D4); the other two sit behind ProtectedRoute+RouteGuard.
import { Kiosk } from './pages/Kiosk.js';
import { Timeclock } from './pages/Timeclock.js';
import { TimeclockManage } from './pages/TimeclockManage.js';
import { WalkerHome } from './pages/WalkerHome.js';
import { KennelHome } from './pages/KennelHome.js';
// Sawyer 08 meds-feeding (A1, A2, G10) — the meds & feeding list and the med pass.
import { MedsFeeding } from './pages/MedsFeeding.js';
import { MedPass } from './pages/MedPass.js';
import { AccountantHome } from './pages/AccountantHome.js';
import { GroomerHome } from './pages/GroomerHome.js';
import { Conversations } from './pages/Conversations.js';
// Sawyer 16 staff-messaging (E4)
import { TeamMessages } from './pages/TeamMessages.js';

type AppProps = {
  config: ClientConfig;
};

/**
 * Role-aware '/' default (spec D3, 2026-07-09 wave): persona roles
 * (groomer/walker/kennel_tech/accountant) navigating to the bare root
 * are redirected to their "My Work" home — their queue IS their
 * dashboard. Desk roles (owner/manager/receptionist) render the
 * facility Dashboard. Deep links elsewhere are untouched; this only
 * owns the '/' default. Redirect uses `replace` so Back doesn't
 * ping-pong through the bounce.
 */
function RoleLanding({ config }: AppProps): JSX.Element {
  const { staff } = useAuth();
  const home = personaHomeRoute(staff?.role);
  if (home) {
    return <Navigate to={home} replace />;
  }
  return (
    <RouteGuard rule={ROUTE_ACCESS_RULES['/']}>
      <Dashboard config={config} />
    </RouteGuard>
  );
}

/**
 * Same contract as the customer-app's App: takes a ClientConfig and returns
 * the React tree. Brand install + route table wire here so `main.tsx` stays
 * a one-liner.
 *
 * Provider order (W0.8):
 *
 *   <AuthProvider>
 *     <NotificationProvider>
 *       <ErrorBoundary>
 *         {/* router lives outside in main.tsx *\/}
 *         <Routes>...</Routes>
 *       </ErrorBoundary>
 *     </NotificationProvider>
 *   </AuthProvider>
 *
 * Existing pages don't change behavior — ProtectedRoute and PlatformAdminRoute
 * still gate the inner shell. New pages opt into useAuth() / useNotifications()
 * as they're ported.
 */
function Routing({ config }: AppProps): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<Login config={config} />} />
      <Route path="/invite/accept" element={<InviteAccept config={config} />} />
      {/* #405 — staff forgot-password. PUBLIC top-level routes (same
          precedent as /invite/accept): a locked-out staffer has no JWT
          to gate behind ProtectedRoute. */}
      <Route
        path="/staff/forgot-password"
        element={<StaffForgotPassword config={config} />}
      />
      <Route
        path="/staff/reset-password"
        element={<StaffResetPassword config={config} />}
      />
      <Route path="/intake/:token" element={<IntakePortal config={config} />} />
      {/* #427 W1 — kiosk time clock: PUBLIC top-level route (precedent:
          /intake/:token). No staff JWT — the tablet authenticates with a
          registered device token; every punch is PIN-verified one-shot
          (spec D4/D11). Full-screen, no AppShell chrome. */}
      <Route path="/kiosk" element={<Kiosk config={config} />} />
      <Route
        element={
          <ProtectedRoute>
            <AppShell config={config} />
          </ProtectedRoute>
        }
      >
        {/* Every tenant operational route is wrapped in RouteGuard so a
            direct-URL to a disallowed surface renders a friendly 403 in
            place (no white screen, no redirect loop) instead of the page.
            Rules come from ROUTE_ACCESS_RULES — the same matrix mirror
            the nav uses and the server (#130/#156) enforces per-route.
            Sub-routes inherit their parent's rule explicitly. */}
        <Route path="/" element={<RoleLanding config={config} />} />
        <Route
          path="/customers"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/customers']}>
              <Customers config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/customers/:id"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/customers']}>
              <CustomerDetail config={config} />
            </RouteGuard>
          }
        />
        {/* #553 — dedicated Pet Profile page. Same guard shape as
            /customers/:id; server enforces pets:read:all on the profile fetch. */}
        <Route
          path="/dogs/:dogId"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/dogs']}>
              <DogDetail config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/incidents"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/incidents']}>
              <Incidents config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/incidents/new"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/incidents']}>
              <IncidentNew config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/incidents/:id"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/incidents']}>
              <IncidentDetail config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/front-desk"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/front-desk']}>
              <FrontDesk config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/bookings"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/bookings']}>
              <Bookings config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/compliance"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/compliance']}>
              <Compliance config={config} />
            </RouteGuard>
          }
        />
        {/* #551 — inbound vaccination records awaiting human verification.
            TWO gates, not one. RouteGuard answers "may this role use it";
            FeatureRoute answers "does this facility have it yet" — the
            surface is meaningless (and its teaching copy is a lie) until
            the inbound mailbox is provisioned server-side. Hiding only the
            nav item would leave the page reachable by URL. */}
        <Route
          path="/vax-inbox"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/vax-inbox']}>
              <FeatureRoute feature="vaxInbox">
                <VaxIntake config={config} />
              </FeatureRoute>
            </RouteGuard>
          }
        />
        <Route
          path="/loyalty"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/loyalty']}>
              <Loyalty config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/pricing-rules"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/pricing-rules']}>
              <PricingRules config={config} />
            </RouteGuard>
          }
        />
        {/* #933 — Owner Settings. URL-addressable tabs: /settings lands on
            the first tab; each tab is its own route so a link to
            /settings/operations opens that card directly. Sub-routes
            inherit the parent's rule explicitly. */}
        <Route
          path="/settings"
          element={<Navigate to="/settings/operations" replace />}
        />
        <Route
          path="/settings/operations"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/settings']}>
              <FeatureRoute feature="ownerSettings">
                <Settings config={config} section="operations" />
              </FeatureRoute>
            </RouteGuard>
          }
        />
        <Route
          path="/settings/capacity"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/settings']}>
              <FeatureRoute feature="ownerSettings">
                <Settings config={config} section="capacity" />
              </FeatureRoute>
            </RouteGuard>
          }
        />
        <Route
          path="/settings/services"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/settings']}>
              <FeatureRoute feature="ownerSettings">
                <Settings config={config} section="services" />
              </FeatureRoute>
            </RouteGuard>
          }
        />
        <Route
          path="/settings/lodging"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/settings']}>
              <FeatureRoute feature="ownerSettings">
                <Settings config={config} section="lodging" />
              </FeatureRoute>
            </RouteGuard>
          }
        />
        <Route
          path="/settings/agreements"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/settings']}>
              <FeatureRoute feature="ownerSettings">
                <Settings config={config} section="agreements" />
              </FeatureRoute>
            </RouteGuard>
          }
        />
        {/* #1212 — texting B1. Same parent rule; the card itself is owner-only
            (the tab is hidden from everyone else, and a hand-typed URL shows
            a plain "only the owner" line, never a dead form). */}
        <Route
          path="/settings/messaging"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/settings']}>
              <FeatureRoute feature="ownerSettings">
                <Settings config={config} section="messaging" />
              </FeatureRoute>
            </RouteGuard>
          }
        />
        {/* Settings → Rewards — what a client sends when they refer a friend. */}
        <Route
          path="/settings/rewards"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/settings']}>
              <FeatureRoute feature="ownerSettings">
                <Settings config={config} section="rewards" />
              </FeatureRoute>
            </RouteGuard>
          }
        />
        {/* Woofers Phase 0 — Settings → AI Assistant (name + AI switches). */}
        <Route
          path="/settings/assistant"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/settings']}>
              <FeatureRoute feature="ownerSettings">
                <Settings config={config} section="assistant" />
              </FeatureRoute>
            </RouteGuard>
          }
        />
        <Route
          path="/payments"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/payments']}>
              <Payments config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/reports"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/reports']}>
              <Reports config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/timesheets"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/timesheets']}>
              <Timesheets config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/schedule"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/schedule']}>
              <Schedule config={config} />
            </RouteGuard>
          }
        />
        {/* Wave 5 (#940 Part A / #919) — every service scheduled on one day,
            the list every rollup number opens. dashboard:read, like the
            dashboard itself (the server gate on /rollup is DASHBOARD_READ). */}
        <Route
          path="/calendar/day"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/calendar/day']}>
              <CalendarDay config={config} />
            </RouteGuard>
          }
        />
        {/* Wave 6 (#939 3b) — the Lodging Calendar, read-only in this step:
            one row per bed, stays as night-spanning blocks in the service
            color, the beds-occupied / threshold header, the boarders with no
            bed beside the grid. Desk trio (the server's gate). */}
        <Route
          path="/calendar/lodging"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/calendar/lodging']}>
              <LodgingCalendar config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/my-schedule"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/my-schedule']}>
              <MySchedule config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/timeclock"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/timeclock']}>
              <Timeclock config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/timeclock/manage"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/timeclock/manage']}>
              <TimeclockManage config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/staff"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/staff']}>
              <Staff config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/audit"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/audit']}>
              <Audit config={config} />
            </RouteGuard>
          }
        />
        {/* Role-specific operational screens (#133/#134/#135/#290). */}
        <Route
          path="/walker"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/walker']}>
              <WalkerHome config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/kennel"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/kennel']}>
              <KennelHome config={config} />
            </RouteGuard>
          }
        />
        {/* Sawyer 08 meds-feeding (A1, A2, G10) */}
        <Route
          path="/meds-feeding"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/meds-feeding']}>
              <MedsFeeding config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/med-pass"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/med-pass']}>
              <MedPass config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/groomer"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/groomer']}>
              <GroomerHome config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/accountant"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/accountant']}>
              <AccountantHome config={config} />
            </RouteGuard>
          }
        />
        {/* #277 — Conversations inbox route. Nav gating via messaging:write
            (owner/manager/receptionist). Page component + server path wired
            by WS-F; this is the route + placeholder shell. */}
        <Route
          path="/conversations"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/conversations']}>
              <Conversations config={config} />
            </RouteGuard>
          }
        />
        {/* Sawyer 16 staff-messaging (E4) — #754, Team messages (board + direct). Role gate:
            staff-messages:read (every tenant role); provisioning gate: the
            server answers GET /api/v2/admin/staff-messages/unread. */}
        <Route
          path="/team-messages/*"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/team-messages']}>
              <FeatureRoute feature="staffMessages">
                <TeamMessages config={config} />
              </FeatureRoute>
            </RouteGuard>
          }
        />
        {/* AI Manager (Lane D2/D5) — AIM staff chat. Path matches the
            module manifest (/ai/manager); rule mirrors the server's
            ai-manager:use:all gate on /api/v2/admin/ai. */}
        <Route
          path="/ai/manager"
          element={
            <RouteGuard rule={ROUTE_ACCESS_RULES['/ai/manager']}>
              <AiManager config={config} />
            </RouteGuard>
          }
        />
        <Route
          path="/platform/tenants"
          element={
            <PlatformAdminRoute>
              <TenantsList config={config} />
            </PlatformAdminRoute>
          }
        />
        <Route
          path="/platform/tenants/new"
          element={
            <PlatformAdminRoute>
              <TenantsNew config={config} />
            </PlatformAdminRoute>
          }
        />
        <Route
          path="/platform/tenants/:tenantId"
          element={
            <PlatformAdminRoute>
              <TenantDetail config={config} />
            </PlatformAdminRoute>
          }
        />
        <Route
          path="/platform/intake/new"
          element={
            <PlatformAdminRoute>
              <IntakeNew config={config} />
            </PlatformAdminRoute>
          }
        />
        <Route
          path="/platform/intake"
          element={
            <PlatformAdminRoute>
              <IntakeList config={config} />
            </PlatformAdminRoute>
          }
        />
        <Route
          path="/platform/intake/:id"
          element={
            <PlatformAdminRoute>
              <IntakeDetail config={config} />
            </PlatformAdminRoute>
          }
        />
        <Route
          path="*"
          element={
            <section className="ferro-admin-page">
              <h1 className="ferro-section__title">Not found</h1>
              <p className="ferro-muted">That page doesn't exist.</p>
            </section>
          }
        />
      </Route>
    </Routes>
  );
}

export function App({ config }: AppProps): JSX.Element {
  useEffect(() => {
    installBrandStyles(config);
  }, [config]);

  return (
    <AuthProvider apiUrl={config.deployment.apiUrl}>
      <NotificationProvider>
        <ErrorBoundary>
          <ServerWakingBanner />
          <Routing config={config} />
        </ErrorBoundary>
      </NotificationProvider>
    </AuthProvider>
  );
}
